import { supabase } from './supabase';
import { readThroughCache, cacheGet, cachePut, isOnline } from './cache';
import { enqueue as enqueueOutbox } from './outbox';

// ============================================================
// Sites
// ============================================================

export type SiteProjectType = 'quick_job' | 'small_refurb' | 'full_site';

export interface Site {
  id: string;
  org_id: string;
  name: string;
  role: string | null;
  address: string | null;
  centre_lat: number | null;
  centre_lng: number | null;
  geofence_radius_m: number | null;
  client_name: string | null;
  principal_designer: string | null;
  site_manager: string | null;
  working_hours: string | null;
  nearest_ae: string | null;
  project_type: SiteProjectType | null;
  in_charge: boolean | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Lists active (non-archived) sites for an org.
 *  Cache-through: returns cached results offline; cache is refreshed on every
 *  successful network read. See lib/cache.ts for the strategy. */
export async function listSites(orgId: string): Promise<Site[]> {
  return readThroughCache<Site>('sites', orgId, async () => {
    const { data, error } = await supabase
      .from('sites')
      .select('*')
      .eq('org_id', orgId)
      .is('archived_at', null)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as Site[];
  });
}

/** Single-site read with cache fallback. Useful when a site URL is opened
 *  directly with no signal — last-known data lets the user keep working. */
export async function getSite(siteId: string): Promise<Site | null> {
  if (!isOnline()) {
    return (await cacheGet<Site>('sites', siteId)) ?? null;
  }
  const { data, error } = await supabase
    .from('sites')
    .select('*')
    .eq('id', siteId)
    .maybeSingle();
  if (error) {
    // Network blip — fall back to cache rather than break the page
    const cached = await cacheGet<Site>('sites', siteId);
    if (cached) return cached;
    throw error;
  }
  const site = (data as Site | null) ?? null;
  if (site) void cachePut<Site>('sites', site.org_id, site.id, site);
  return site;
}

export async function createSite(input: {
  orgId: string;
  name: string;
  role?: string;
  address?: string | null;
  centreLat?: number | null;
  centreLng?: number | null;
  geofenceRadiusM?: number | null;
  projectType?: SiteProjectType | null;
  inCharge?: boolean | null;
}): Promise<Site> {
  const { data, error } = await supabase
    .from('sites')
    .insert({
      org_id: input.orgId,
      name: input.name,
      role: input.role ?? 'Principal Contractor',
      address: input.address ?? null,
      centre_lat: input.centreLat ?? null,
      centre_lng: input.centreLng ?? null,
      geofence_radius_m: input.geofenceRadiusM ?? 200,
      project_type: input.projectType ?? null,
      in_charge: input.inCharge ?? null,
    })
    .select()
    .single();
  if (error) throw error;
  return data as Site;
}

export async function archiveSite(siteId: string): Promise<void> {
  const { error } = await supabase
    .from('sites')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', siteId);
  if (error) throw error;
}

/** Lists archived (completed) sites for an org. Newest archive first. */
export async function listArchivedSites(orgId: string): Promise<Site[]> {
  const { data, error } = await supabase
    .from('sites')
    .select('*')
    .eq('org_id', orgId)
    .not('archived_at', 'is', null)
    .order('archived_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as Site[];
}

/** Un-archive a previously archived site (restore to active list). */
export async function restoreSite(siteId: string): Promise<void> {
  const { error } = await supabase
    .from('sites')
    .update({ archived_at: null })
    .eq('id', siteId);
  if (error) throw error;
}

/**
 * Hard delete a site AND every sign-in, RAMS, form, permit and document
 * attached to it. Cascade is handled by ON DELETE CASCADE on the relevant
 * foreign keys. This is permanent — only call after a strong confirmation
 * (e.g. type-the-name pattern).
 */
export async function deleteSite(siteId: string): Promise<void> {
  const { error } = await supabase
    .from('sites')
    .delete()
    .eq('id', siteId);
  if (error) throw error;
}

export async function updateSite(siteId: string, patch: Partial<Site>): Promise<Site> {
  const { data, error } = await supabase
    .from('sites')
    .update(patch)
    .eq('id', siteId)
    .select()
    .single();
  if (error) throw error;
  return data as Site;
}

// ============================================================
// Sign-ins
// ============================================================

export interface SignIn {
  id: string;
  org_id: string;
  site_id: string;
  full_name: string;
  company: string;
  trade_role: string | null;
  mobile: string | null;
  vehicle_reg: string | null;
  override_reason: string | null;
  time_in: string;
  time_out: string | null;
  signature_in_url: string | null;
  signature_out_url: string | null;
  in_lat: number | null;
  in_lng: number | null;
  in_address: string | null;
  in_accuracy_m: number | null;
  out_lat: number | null;
  out_lng: number | null;
  out_address: string | null;
  out_accuracy_m: number | null;
  confirmations_accepted: boolean[];
  is_first_aider: boolean;
  // True if the sign-in was accepted despite the company not being on the
  // inducted-subbies list. Set by SignInForm when companyCheck === 'unknown'
  // so an org admin can follow up later. Defaults false for the normal path.
  requires_induction: boolean;
  // 'worker' (default) = full induction/RAMS confirmations; 'visitor' = short
  // form for delivery drivers, surveyors, inspectors etc. with a host instead
  // of a trade and no induction check.
  sign_in_type: 'worker' | 'visitor';
  visitor_host: string | null;
  created_at: string;
}

export async function listActiveSignIns(siteId: string): Promise<SignIn[]> {
  const { data, error } = await supabase
    .from('sign_ins')
    .select('*')
    .eq('site_id', siteId)
    .is('time_out', null)
    .order('time_in', { ascending: false });
  if (error) throw error;
  return (data ?? []) as SignIn[];
}

/**
 * Look up whether a given person (matched on name + company, case-insensitive)
 * has previously signed in as a first aider on any site for this org. Used
 * by the SignInForm to pre-tick the "first aider" checkbox when the user
 * picks a recent-ops chip or types the same name as before.
 */
export async function lookupFirstAiderStatus(
  orgId: string,
  fullName: string,
  company: string,
): Promise<boolean> {
  const n = fullName.trim();
  const c = company.trim();
  if (!n || !c) return false;
  const { data, error } = await supabase
    .from('sign_ins')
    .select('id')
    .eq('org_id', orgId)
    .ilike('full_name', n)
    .ilike('company', c)
    .eq('is_first_aider', true)
    .limit(1);
  if (error) return false;
  return (data ?? []).length > 0;
}

export async function listTodaySignIns(siteId: string): Promise<SignIn[]> {
  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  const { data, error } = await supabase
    .from('sign_ins')
    .select('*')
    .eq('site_id', siteId)
    .gte('time_in', startOfDay.toISOString())
    .order('time_in', { ascending: false });
  if (error) throw error;
  return (data ?? []) as SignIn[];
}

export interface CreateSignInInput {
  orgId: string;
  siteId: string;
  full_name: string;
  company: string;
  trade_role: string | null;
  mobile: string | null;
  vehicle_reg: string | null;
  override_reason?: string | null;
  in_lat: number | null;
  in_lng: number | null;
  in_address: string | null;
  in_accuracy_m: number | null;
  confirmations_accepted: boolean[];
  is_first_aider?: boolean;
  /** True when the company wasn't found in the inducted-subbies list — the
   *  form accepted the sign-in anyway. Org admin needs to follow up before
   *  this worker's next visit. */
  requires_induction?: boolean;
  /** 'worker' (default) or 'visitor'. Visitors are delivery drivers,
   *  surveyors, inspectors etc — short form, no induction check, host name
   *  captured instead of a trade. */
  sign_in_type?: 'worker' | 'visitor';
  /** For visitor sign-ins: the person on site they're here to see. */
  visitor_host?: string | null;
}

/**
 * Create a sign-in. If we're offline (or the network insert fails), the
 * record gets queued in the outbox and replayed when signal returns.
 *
 * Returns a SignIn object. When offline we return a "shadow" record with
 * a temp id so the UI can keep working — that id is replaced with the
 * real server id when the outbox drains. Pages that depend on the id
 * being a server id should listen for the outbox-replayed event or
 * re-read the list once online.
 */
export async function createSignIn(input: CreateSignInInput): Promise<SignIn> {
  const payload = {
    orgId: input.orgId,
    siteId: input.siteId,
    full_name: input.full_name,
    company: input.company,
    trade_role: input.trade_role,
    mobile: input.mobile,
    vehicle_reg: input.vehicle_reg,
    override_reason: input.override_reason ?? null,
    in_lat: input.in_lat,
    in_lng: input.in_lng,
    in_address: input.in_address,
    in_accuracy_m: input.in_accuracy_m,
    confirmations_accepted: input.confirmations_accepted,
    is_first_aider: input.is_first_aider ?? false,
    requires_induction: input.requires_induction ?? false,
    sign_in_type: input.sign_in_type ?? 'worker',
    visitor_host: input.visitor_host ?? null,
    time_in: new Date().toISOString(),
  };

  // Offline path — queue immediately and return a temp record so the UI
  // shows "this person is signed in" without a network round-trip.
  if (!isOnline()) {
    const tempId = await enqueueOutbox('sign_in.create', input.orgId, payload);
    return {
      id: tempId,
      org_id: input.orgId,
      site_id: input.siteId,
      full_name: input.full_name,
      company: input.company,
      trade_role: input.trade_role,
      mobile: input.mobile,
      vehicle_reg: input.vehicle_reg,
      override_reason: input.override_reason ?? null,
      time_in: payload.time_in,
      time_out: null,
      signature_in_url: null,
      signature_out_url: null,
      in_lat: input.in_lat,
      in_lng: input.in_lng,
      in_address: input.in_address,
      in_accuracy_m: input.in_accuracy_m,
      out_lat: null,
      out_lng: null,
      out_address: null,
      out_accuracy_m: null,
      confirmations_accepted: input.confirmations_accepted,
      is_first_aider: input.is_first_aider ?? false,
      requires_induction: input.requires_induction ?? false,
      sign_in_type: input.sign_in_type ?? 'worker',
      visitor_host: input.visitor_host ?? null,
      created_at: payload.time_in,
    } as SignIn;
  }

  try {
    const { data, error } = await supabase
      .from('sign_ins')
      .insert({
        org_id: input.orgId,
        site_id: input.siteId,
        full_name: input.full_name,
        company: input.company,
        trade_role: input.trade_role,
        mobile: input.mobile,
        vehicle_reg: input.vehicle_reg,
        override_reason: input.override_reason ?? null,
        in_lat: input.in_lat,
        in_lng: input.in_lng,
        in_address: input.in_address,
        in_accuracy_m: input.in_accuracy_m,
        confirmations_accepted: input.confirmations_accepted,
        is_first_aider: input.is_first_aider ?? false,
        requires_induction: input.requires_induction ?? false,
        sign_in_type: input.sign_in_type ?? 'worker',
        visitor_host: input.visitor_host ?? null,
      })
      .select()
      .single();
    if (error) throw error;
    return data as SignIn;
  } catch (e) {
    // Network blip mid-flight — queue rather than lose the record
    if (!isOnline()) {
      const tempId = await enqueueOutbox('sign_in.create', input.orgId, payload);
      return {
        id: tempId,
        org_id: input.orgId,
        site_id: input.siteId,
        full_name: input.full_name,
        company: input.company,
        trade_role: input.trade_role,
        mobile: input.mobile,
        vehicle_reg: input.vehicle_reg,
        override_reason: input.override_reason ?? null,
        time_in: payload.time_in,
        time_out: null,
        signature_in_url: null,
        signature_out_url: null,
        in_lat: input.in_lat,
        in_lng: input.in_lng,
        in_address: input.in_address,
        in_accuracy_m: input.in_accuracy_m,
        out_lat: null,
        out_lng: null,
        out_address: null,
        out_accuracy_m: null,
        confirmations_accepted: input.confirmations_accepted,
        is_first_aider: input.is_first_aider ?? false,
        requires_induction: input.requires_induction ?? false,
        sign_in_type: input.sign_in_type ?? 'worker',
        visitor_host: input.visitor_host ?? null,
        created_at: payload.time_in,
      } as SignIn;
    }
    throw e;
  }
}

export async function setSignInSignaturePath(
  signInId: string,
  side: 'in' | 'out',
  path: string
): Promise<void> {
  const col = side === 'in' ? 'signature_in_url' : 'signature_out_url';
  const { error } = await supabase
    .from('sign_ins')
    .update({ [col]: path })
    .eq('id', signInId);
  if (error) throw error;
}

export async function signOutPerson(
  signInId: string,
  geo: { lat: number | null; lng: number | null; address: string | null; accuracy_m: number | null }
): Promise<void> {
  const { error } = await supabase
    .from('sign_ins')
    .update({
      time_out: new Date().toISOString(),
      out_lat: geo.lat,
      out_lng: geo.lng,
      out_address: geo.address,
      out_accuracy_m: geo.accuracy_m,
    })
    .eq('id', signInId);
  if (error) throw error;
}

/**
 * Bulk sign-out everyone still active on a site, at a specified time.
 * Used by the "Close site" end-of-day flow.
 */
export async function bulkSignOut(
  siteId: string,
  signOutTimeIso: string,
): Promise<{ signedOut: number }> {
  // Find every active sign-in (no time_out)
  const { data: actives, error: e1 } = await supabase
    .from('sign_ins')
    .select('id')
    .eq('site_id', siteId)
    .is('time_out', null);
  if (e1) throw e1;
  const ids = (actives ?? []).map((a) => a.id);
  if (ids.length === 0) return { signedOut: 0 };
  const { error: e2 } = await supabase
    .from('sign_ins')
    .update({ time_out: signOutTimeIso })
    .in('id', ids);
  if (e2) throw e2;
  return { signedOut: ids.length };
}

/**
 * List recent unique people who've signed in on this site, deduped by
 * (full_name + company), most-recent first. Used to power the "tap your
 * name" quick-pick on the sign-in form.
 */
export async function listRecentOpsOnSite(
  siteId: string,
  limitPeople = 30,
  daysBack = 90,
): Promise<{ full_name: string; company: string; trade_role: string | null; mobile: string | null; vehicle_reg: string | null; last_signed_in: string }[]> {
  const since = new Date(Date.now() - daysBack * 86400000).toISOString();
  const { data, error } = await supabase
    .from('sign_ins')
    .select('full_name, company, trade_role, mobile, vehicle_reg, time_in')
    .eq('site_id', siteId)
    .gte('time_in', since)
    .order('time_in', { ascending: false });
  if (error) throw error;
  // Dedupe by name+company, keeping the most recent row's details
  const seen = new Map<string, { full_name: string; company: string; trade_role: string | null; mobile: string | null; vehicle_reg: string | null; last_signed_in: string }>();
  for (const row of data ?? []) {
    const key = `${row.full_name.trim().toLowerCase()}|${row.company.trim().toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.set(key, {
      full_name: row.full_name,
      company: row.company,
      trade_role: row.trade_role,
      mobile: row.mobile,
      vehicle_reg: row.vehicle_reg,
      last_signed_in: row.time_in,
    });
    if (seen.size >= limitPeople) break;
  }
  return Array.from(seen.values());
}

/**
 * Pre-fill source for the sign-in form. Combines:
 *   - **Recent ops** — people who've signed in on THIS site in the last 90 days
 *   - **Inducted people** — everyone inducted via the subbie induction form,
 *     org-wide, who hasn't appeared in recent-ops (they've been inducted but
 *     never signed in here yet)
 *
 * Both sources are deduped by (name + company), case-insensitive. Recent-ops
 * wins over inducted because the recent-ops record has the most current
 * details (mobile may have changed, etc.). Each entry includes `source` so
 * the UI can render a small label ("inducted" vs no label for "recent").
 *
 * The first-aider flag is hydrated from the subbie register where it exists,
 * so tapping a chip pre-ticks the "I'm a qualified first aider" box.
 */
export interface SignInPrefillSource {
  full_name: string;
  company: string;
  trade_role: string | null;
  mobile: string | null;
  vehicle_reg: string | null;
  last_signed_in: string | null;
  is_first_aider: boolean;
  source: 'recent' | 'inducted';
}

export async function listSignInPrefillSources(
  orgId: string,
  siteId: string,
  daysBack = 90,
  limitPeople = 30,
): Promise<SignInPrefillSource[]> {
  const since = new Date(Date.now() - daysBack * 86400000).toISOString();

  // ----- 1. Recent ops from THIS site -----
  const { data: signIns } = await supabase
    .from('sign_ins')
    .select('full_name, company, trade_role, mobile, vehicle_reg, time_in, is_first_aider')
    .eq('site_id', siteId)
    .gte('time_in', since)
    .order('time_in', { ascending: false });

  const seen = new Map<string, SignInPrefillSource>();
  for (const row of signIns ?? []) {
    const key = `${(row.full_name as string).trim().toLowerCase()}|${(row.company as string).trim().toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.set(key, {
      full_name: row.full_name as string,
      company: row.company as string,
      trade_role: row.trade_role as string | null,
      mobile: row.mobile as string | null,
      vehicle_reg: row.vehicle_reg as string | null,
      last_signed_in: row.time_in as string,
      is_first_aider: !!(row as { is_first_aider?: boolean }).is_first_aider,
      source: 'recent',
    });
    if (seen.size >= limitPeople) break;
  }

  // ----- 2. Inducted people org-wide who aren't already in recent ops -----
  // Join inductions onto subcontractors to pull mobile, trade, first_aider.
  // This is a single query — Supabase's PostgREST handles the join via the
  // foreign-key reference name.
  const { data: inductions } = await supabase
    .from('subcontractor_inductions')
    .select(`
      person_name,
      inducted_at,
      subcontractor:subcontractors!subcontractor_inductions_subcontractor_id_fkey (
        company_name, trade, mobile, first_aider, archived_at
      )
    `)
    .eq('org_id', orgId)
    .order('inducted_at', { ascending: false });

  for (const row of inductions ?? []) {
    const r = row as unknown as {
      person_name: string;
      inducted_at: string;
      subcontractor: {
        company_name: string;
        trade: string | null;
        mobile: string | null;
        first_aider: boolean;
        archived_at: string | null;
      } | null;
    };
    if (!r.subcontractor || r.subcontractor.archived_at) continue;
    const name = (r.person_name ?? '').trim();
    const company = (r.subcontractor.company_name ?? '').trim();
    if (!name || !company) continue;
    const key = `${name.toLowerCase()}|${company.toLowerCase()}`;
    if (seen.has(key)) continue;   // Already in recent ops — recent-ops detail wins.
    seen.set(key, {
      full_name: name,
      company,
      trade_role: r.subcontractor.trade,
      mobile: r.subcontractor.mobile,
      vehicle_reg: null,
      last_signed_in: null,
      is_first_aider: r.subcontractor.first_aider,
      source: 'inducted',
    });
    if (seen.size >= limitPeople * 2) break;
  }

  return Array.from(seen.values());
}

/** Get all sign-ins for a site on a specific calendar day (00:00 to 23:59). */
export async function listSignInsForDay(siteId: string, day: Date): Promise<SignIn[]> {
  const start = new Date(day); start.setHours(0, 0, 0, 0);
  const end = new Date(day);   end.setHours(23, 59, 59, 999);
  const { data, error } = await supabase
    .from('sign_ins')
    .select('*')
    .eq('site_id', siteId)
    .gte('time_in', start.toISOString())
    .lte('time_in', end.toISOString())
    .order('time_in', { ascending: true });
  if (error) throw error;
  return (data ?? []) as SignIn[];
}

/**
 * All sign-ins for a site between fromIso (inclusive) and toIso (exclusive).
 * Used by the weekly timesheet.
 */
export async function listSignInsBetween(
  siteId: string,
  fromIso: string,
  toIso: string,
): Promise<SignIn[]> {
  const { data, error } = await supabase
    .from('sign_ins')
    .select('*')
    .eq('site_id', siteId)
    .gte('time_in', fromIso)
    .lt('time_in', toIso)
    .order('time_in', { ascending: true });
  if (error) throw error;
  return (data ?? []) as SignIn[];
}

// ============================================================
// Tick list runs
// ============================================================

export type TickItemResult = 'pass' | 'fail' | 'na';

export interface TickItemResponse {
  state: TickItemResult;
  notes?: string;
  photo_paths?: string[];                // storage paths in `accident-photos` bucket
  resolved?: boolean;                    // set when an issue is closed out
  resolved_at?: string;                  // iso timestamp
  resolved_by_name?: string;
  resolution_notes?: string;             // what was done to resolve
}

/** A flattened "open issue" derived from tick list runs. */
export interface OpenIssue {
  run_id: string;
  run_kind: TickListRun['kind'];
  run_at: string;
  item_key: string;
  item_label?: string;            // resolved at the page level from templates
  notes?: string;
  photo_paths?: string[];
  age_days: number;
}

/**
 * List unresolved issues across recent tick list runs for a site.
 * Looks back 90 days by default.
 */
export async function listOpenIssues(siteId: string, daysBack = 90): Promise<OpenIssue[]> {
  const since = new Date(Date.now() - daysBack * 86400000).toISOString();
  const { data, error } = await supabase
    .from('tick_list_runs')
    .select('id, kind, responses, performed_at, created_at')
    .eq('site_id', siteId)
    .gte('created_at', since)
    .order('created_at', { ascending: false });
  if (error) throw error;
  const out: OpenIssue[] = [];
  for (const run of data ?? []) {
    const responses = (run.responses ?? {}) as TickResponses;
    for (const [key, resp] of Object.entries(responses)) {
      if (resp?.state !== 'fail') continue;
      if (resp?.resolved === true) continue;
      const at = new Date(run.created_at);
      const ageDays = Math.floor((Date.now() - at.getTime()) / 86400000);
      out.push({
        run_id: run.id,
        run_kind: run.kind,
        run_at: run.created_at,
        item_key: key,
        notes: resp.notes,
        photo_paths: resp.photo_paths,
        age_days: ageDays,
      });
    }
  }
  return out;
}

/** Mark a single tick-list issue resolved, with notes. */
export async function resolveTickIssue(
  runId: string,
  itemKey: string,
  resolverName: string,
  resolutionNotes: string,
): Promise<void> {
  // Read current responses
  const { data: run, error: e1 } = await supabase
    .from('tick_list_runs')
    .select('responses')
    .eq('id', runId)
    .single();
  if (e1) throw e1;
  const responses = ((run?.responses ?? {}) as TickResponses);
  const item = responses[itemKey];
  if (!item) throw new Error('Item not found in this tick list run.');
  responses[itemKey] = {
    ...item,
    resolved: true,
    resolved_at: new Date().toISOString(),
    resolved_by_name: resolverName,
    resolution_notes: resolutionNotes,
  };
  const { error: e2 } = await supabase
    .from('tick_list_runs')
    .update({ responses })
    .eq('id', runId);
  if (e2) throw e2;
}

export type TickResponses = Record<string, TickItemResponse>;

export interface TickListRun {
  id: string;
  org_id: string;
  site_id: string;
  kind: 'daily' | 'weekly' | 'monthly' | 'telehandler_weekly';
  performed_by: string | null;
  performed_by_name: string | null;
  performed_at: string;
  responses: TickResponses;
  notes: string | null;
  pdf_url: string | null;
  created_at: string;
}

export async function listTickRuns(
  siteId: string,
  kind?: TickListRun['kind'],
  limit = 50
): Promise<TickListRun[]> {
  let q = supabase
    .from('tick_list_runs')
    .select('*')
    .eq('site_id', siteId)
    .order('performed_at', { ascending: false })
    .limit(limit);
  if (kind) q = q.eq('kind', kind);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as TickListRun[];
}

export interface CreateTickRunInput {
  id?: string;             // pre-generated UUID so the client can construct
                           // photo storage paths before insert.
  orgId: string;
  siteId: string;
  kind: TickListRun['kind'];
  performed_by_name: string;
  responses: TickResponses;
  notes: string | null;
}

export async function createTickRun(input: CreateTickRunInput): Promise<TickListRun> {
  const row: Record<string, unknown> = {
    org_id: input.orgId,
    site_id: input.siteId,
    kind: input.kind,
    performed_by_name: input.performed_by_name,
    responses: input.responses,
    notes: input.notes,
  };
  if (input.id) row.id = input.id;
  const { data, error } = await supabase
    .from('tick_list_runs')
    .insert(row)
    .select()
    .single();
  if (error) throw error;
  return data as TickListRun;
}

export async function getLatestTickRun(
  siteId: string,
  kind: TickListRun['kind']
): Promise<TickListRun | null> {
  const { data, error } = await supabase
    .from('tick_list_runs')
    .select('*')
    .eq('site_id', siteId)
    .eq('kind', kind)
    .order('performed_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data as TickListRun | null) ?? null;
}

// ============================================================
// Forms (toolbox talk, accident/near-miss, subbie induction)
// ============================================================

export type FormKind = 'toolbox_talk' | 'accident' | 'subcontractor_induction' | 'other';

export interface FormRow {
  id: string;
  org_id: string;
  site_id: string | null;
  kind: FormKind;
  title: string | null;
  performed_by: string | null;
  performed_by_name: string | null;
  performed_at: string;
  data: Record<string, unknown>;
  signature_url: string | null;
  pdf_url: string | null;
  is_riddor: boolean | null;
  riddor_flags: Record<string, boolean> | null;
  attendee_count: number | null;
  created_at: string;
  updated_at: string;
}

export interface FormAttendeeRow {
  id: string;
  form_id: string;
  full_name: string;
  company: string | null;
  signature_url: string | null;
  verbal_only: boolean;
  created_at: string;
}

export interface CreateFormInput {
  id?: string;
  orgId: string;
  siteId: string;
  kind: FormKind;
  title: string;
  performed_by_name: string;
  data: Record<string, unknown>;
  is_riddor?: boolean;
  riddor_flags?: Record<string, boolean> | null;
  attendee_count?: number;
}

export async function createForm(input: CreateFormInput): Promise<FormRow> {
  const row: Record<string, unknown> = {
    org_id: input.orgId,
    site_id: input.siteId,
    kind: input.kind,
    title: input.title,
    performed_by_name: input.performed_by_name,
    data: input.data,
    is_riddor: input.is_riddor ?? false,
    riddor_flags: input.riddor_flags ?? null,
    attendee_count: input.attendee_count ?? null,
  };
  if (input.id) row.id = input.id;
  const { data, error } = await supabase
    .from('forms')
    .insert(row)
    .select()
    .single();
  if (error) throw error;
  return data as FormRow;
}

/** Append a follow-up entry to a form's investigation timeline. */
export async function appendFormTimelineEntry(formId: string, entry: {
  note: string;
  by_name: string;
  photo_paths?: string[];
}): Promise<void> {
  const { data: row, error: e1 } = await supabase
    .from('forms')
    .select('data')
    .eq('id', formId)
    .single();
  if (e1) throw e1;
  const data = (row?.data ?? {}) as Record<string, unknown>;
  const timeline = Array.isArray((data as { timeline?: unknown[] }).timeline)
    ? ((data as { timeline: unknown[] }).timeline as Array<unknown>).slice()
    : [];
  timeline.push({
    at: new Date().toISOString(),
    by_name: entry.by_name,
    note: entry.note,
    photo_paths: entry.photo_paths ?? [],
  });
  const newData = { ...data, timeline };
  const { error: e2 } = await supabase.from('forms').update({ data: newData }).eq('id', formId);
  if (e2) throw e2;
}

/** Update an existing form row (e.g. for RIDDOR ref backfill or other late edits). */
export async function updateForm(formId: string, patch: {
  title?: string | null;
  performed_by_name?: string | null;
  performed_at?: string;
  data?: Record<string, unknown>;
  is_riddor?: boolean | null;
  riddor_flags?: Record<string, boolean> | null;
}): Promise<void> {
  const { error } = await supabase
    .from('forms')
    .update(patch)
    .eq('id', formId);
  if (error) throw error;
}

export async function setFormSignaturePath(formId: string, path: string): Promise<void> {
  const { error } = await supabase
    .from('forms')
    .update({ signature_url: path })
    .eq('id', formId);
  if (error) throw error;
}

export async function listForms(siteId: string, kind?: FormKind, limit = 50): Promise<FormRow[]> {
  let q = supabase
    .from('forms')
    .select('*')
    .eq('site_id', siteId)
    .order('performed_at', { ascending: false })
    .limit(limit);
  if (kind) q = q.eq('kind', kind);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as FormRow[];
}

export async function getForm(formId: string): Promise<FormRow | null> {
  const { data, error } = await supabase.from('forms').select('*').eq('id', formId).maybeSingle();
  if (error) throw error;
  return (data as FormRow | null) ?? null;
}

export async function listFormAttendees(formId: string): Promise<FormAttendeeRow[]> {
  const { data, error } = await supabase
    .from('form_attendees')
    .select('*')
    .eq('form_id', formId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as FormAttendeeRow[];
}

export async function createFormAttendee(input: {
  form_id: string;
  full_name: string;
  company: string | null;
  signature_url: string | null;
  verbal_only?: boolean;
}): Promise<FormAttendeeRow> {
  const { data, error } = await supabase
    .from('form_attendees')
    .insert(input)
    .select()
    .single();
  if (error) throw error;
  return data as FormAttendeeRow;
}

// ============================================================
// Subcontractors
// ============================================================

export interface SubcontractorRow {
  id: string;
  org_id: string;
  company_name: string;
  trade: string | null;
  main_contact: string | null;
  mobile: string | null;
  address: string | null;
  pl_insurance_expiry: string | null;
  el_insurance_expiry: string | null;
  pi_insurance_expiry: string | null;
  insurance_files: string[];
  approved_by: string | null;
  approved_at: string | null;
  archived_at: string | null;
  first_aider: boolean;
  first_aid_cert_expiry: string | null;
  created_at: string;
  updated_at: string;
}

export async function findOrCreateSubcontractor(input: {
  orgId: string;
  company_name: string;
  trade: string | null;
  main_contact: string | null;
  mobile: string | null;
  address: string | null;
  pl_insurance_expiry: string | null;
  el_insurance_expiry: string | null;
  approved_by: string | null;
  first_aider?: boolean;
  first_aid_cert_expiry?: string | null;
}): Promise<SubcontractorRow> {
  // Look up by case-insensitive company name within the org.
  const { data: existing, error: findErr } = await supabase
    .from('subcontractors')
    .select('*')
    .eq('org_id', input.orgId)
    .ilike('company_name', input.company_name)
    .is('archived_at', null)
    .limit(1);
  if (findErr) throw findErr;
  if (existing && existing.length > 0) {
    // Patch with any new insurance dates
    const ex = existing[0] as SubcontractorRow;
    const patch: Record<string, unknown> = {};
    if (input.pl_insurance_expiry && input.pl_insurance_expiry !== ex.pl_insurance_expiry) {
      patch.pl_insurance_expiry = input.pl_insurance_expiry;
    }
    if (input.el_insurance_expiry && input.el_insurance_expiry !== ex.el_insurance_expiry) {
      patch.el_insurance_expiry = input.el_insurance_expiry;
    }
    if (input.trade && !ex.trade) patch.trade = input.trade;
    if (input.mobile && !ex.mobile) patch.mobile = input.mobile;
    if (input.address && !ex.address) patch.address = input.address;
    // First-aider flag is "sticky once true" — promoting someone to first
    // aider sticks across re-inductions. To demote, the user edits the
    // subbie register directly.
    if (input.first_aider === true && !ex.first_aider) patch.first_aider = true;
    if (input.first_aid_cert_expiry && input.first_aid_cert_expiry !== ex.first_aid_cert_expiry) {
      patch.first_aid_cert_expiry = input.first_aid_cert_expiry;
    }
    if (Object.keys(patch).length > 0) {
      const { data: upd, error: updErr } = await supabase
        .from('subcontractors')
        .update(patch)
        .eq('id', ex.id)
        .select()
        .single();
      if (updErr) throw updErr;
      return upd as SubcontractorRow;
    }
    return ex;
  }
  const { data: created, error: createErr } = await supabase
    .from('subcontractors')
    .insert({
      org_id: input.orgId,
      company_name: input.company_name,
      trade: input.trade,
      main_contact: input.main_contact,
      mobile: input.mobile,
      address: input.address,
      pl_insurance_expiry: input.pl_insurance_expiry,
      el_insurance_expiry: input.el_insurance_expiry,
      approved_by: input.approved_by,
      approved_at: new Date().toISOString(),
      first_aider: input.first_aider ?? false,
      first_aid_cert_expiry: input.first_aid_cert_expiry ?? null,
    })
    .select()
    .single();
  if (createErr) throw createErr;
  return created as SubcontractorRow;
}

export async function createSubcontractorInduction(input: {
  org_id: string;
  subcontractor_id: string;
  site_id: string | null;
  person_name: string;
  form_id: string | null;
}): Promise<void> {
  const { error } = await supabase.from('subcontractor_inductions').insert(input);
  if (error) throw error;
}

// ============================================================
// Plant register
// ============================================================

export interface PlantItem {
  id: string;
  org_id: string;
  name: string;
  serial: string | null;
  loler_date: string | null;
  pat_date: string | null;
  notes: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export async function listPlant(orgId: string): Promise<PlantItem[]> {
  return readThroughCache<PlantItem>('plant', orgId, async () => {
    const { data, error } = await supabase
      .from('plant_items')
      .select('*')
      .eq('org_id', orgId)
      .is('archived_at', null)
      .order('name', { ascending: true });
    if (error) throw error;
    return (data ?? []) as PlantItem[];
  });
}

export async function createPlant(input: {
  orgId: string;
  name: string;
  serial: string | null;
  loler_date: string | null;
  pat_date: string | null;
  notes: string | null;
}): Promise<PlantItem> {
  const { data, error } = await supabase
    .from('plant_items')
    .insert({
      org_id: input.orgId,
      name: input.name,
      serial: input.serial,
      loler_date: input.loler_date,
      pat_date: input.pat_date,
      notes: input.notes,
    })
    .select()
    .single();
  if (error) throw error;
  return data as PlantItem;
}

export async function updatePlant(id: string, patch: Partial<PlantItem>): Promise<void> {
  const { error } = await supabase.from('plant_items').update(patch).eq('id', id);
  if (error) throw error;
}

export async function archivePlant(id: string): Promise<void> {
  const { error } = await supabase
    .from('plant_items')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

// ============================================================
// COSHH register
// ============================================================

export interface CoshhItem {
  id: string;
  org_id: string;
  product: string;
  used_for: string | null;
  hazards: string | null;
  controls: string | null;
  sds_file_url: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Cache-through. COSHH safety data is the prototypical "I'm on a basement
 *  level, no signal, and I need to know what to do if this stuff splashes
 *  in someone's eye" use case. Caching it is the whole point of offline. */
export async function listCoshh(orgId: string): Promise<CoshhItem[]> {
  return readThroughCache<CoshhItem>('coshh', orgId, async () => {
    const { data, error } = await supabase
      .from('coshh_items')
      .select('*')
      .eq('org_id', orgId)
      .is('archived_at', null)
      .order('product', { ascending: true });
    if (error) throw error;
    return (data ?? []) as CoshhItem[];
  });
}

export async function createCoshh(input: {
  orgId: string;
  product: string;
  used_for: string | null;
  hazards: string | null;
  controls: string | null;
}): Promise<CoshhItem> {
  const { data, error } = await supabase
    .from('coshh_items')
    .insert({
      org_id: input.orgId,
      product: input.product,
      used_for: input.used_for,
      hazards: input.hazards,
      controls: input.controls,
    })
    .select()
    .single();
  if (error) throw error;
  return data as CoshhItem;
}

export async function updateCoshh(id: string, patch: Partial<CoshhItem>): Promise<void> {
  const { error } = await supabase.from('coshh_items').update(patch).eq('id', id);
  if (error) throw error;
}

export async function archiveCoshh(id: string): Promise<void> {
  const { error } = await supabase
    .from('coshh_items')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

// ============================================================
// Training register — people + per-card records
// ============================================================

export interface TrainingPerson {
  id: string;
  org_id: string;
  full_name: string;
  notes: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TrainingRecord {
  id: string;
  org_id: string;
  person_id: string;
  card_type: string;
  card_number: string | null;
  expires_on: string | null;
  card_image_url: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export async function listTrainingPeople(orgId: string): Promise<TrainingPerson[]> {
  const { data, error } = await supabase
    .from('training_people')
    .select('*')
    .eq('org_id', orgId)
    .is('archived_at', null)
    .order('full_name', { ascending: true });
  if (error) throw error;
  return (data ?? []) as TrainingPerson[];
}

export async function listTrainingRecordsForPerson(personId: string): Promise<TrainingRecord[]> {
  const { data, error } = await supabase
    .from('training_records')
    .select('*')
    .eq('person_id', personId);
  if (error) throw error;
  return (data ?? []) as TrainingRecord[];
}

export async function listAllTrainingRecords(orgId: string): Promise<TrainingRecord[]> {
  return readThroughCache<TrainingRecord>('training', orgId, async () => {
    const { data, error } = await supabase
      .from('training_records')
      .select('*')
      .eq('org_id', orgId);
    if (error) throw error;
    return (data ?? []) as TrainingRecord[];
  });
}

export async function createTrainingPerson(input: {
  orgId: string;
  full_name: string;
  notes: string | null;
}): Promise<TrainingPerson> {
  const { data, error } = await supabase
    .from('training_people')
    .insert({ org_id: input.orgId, full_name: input.full_name, notes: input.notes })
    .select()
    .single();
  if (error) throw error;
  return data as TrainingPerson;
}

export async function updateTrainingPerson(id: string, patch: Partial<TrainingPerson>): Promise<void> {
  const { error } = await supabase.from('training_people').update(patch).eq('id', id);
  if (error) throw error;
}

export async function archiveTrainingPerson(id: string): Promise<void> {
  const { error } = await supabase
    .from('training_people')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function upsertTrainingRecord(input: {
  orgId: string;
  person_id: string;
  card_type: string;
  expires_on: string | null;
}): Promise<void> {
  // (person_id, card_type) is unique per the schema. Use upsert on that pair.
  const { error } = await supabase
    .from('training_records')
    .upsert(
      {
        org_id: input.orgId,
        person_id: input.person_id,
        card_type: input.card_type,
        expires_on: input.expires_on,
      },
      { onConflict: 'person_id,card_type' }
    );
  if (error) throw error;
}

export async function deleteTrainingRecord(personId: string, card_type: string): Promise<void> {
  const { error } = await supabase
    .from('training_records')
    .delete()
    .eq('person_id', personId)
    .eq('card_type', card_type);
  if (error) throw error;
}

// ============================================================
// Subcontractor register (read/list)
// ============================================================

/**
 * Look up a subcontractor by company name within an org. Returns null if not found.
 * Used by the sign-in flow to detect whether a subbie has been inducted yet.
 */
export async function findSubcontractorByCompany(
  orgId: string,
  companyName: string,
): Promise<SubcontractorRow | null> {
  const trimmed = companyName.trim();
  if (!trimmed) return null;
  const { data, error } = await supabase
    .from('subcontractors')
    .select('*')
    .eq('org_id', orgId)
    .ilike('company_name', trimmed)
    .is('archived_at', null)
    .limit(1);
  if (error) return null;
  return (data && data.length > 0) ? (data[0] as SubcontractorRow) : null;
}

export async function listSubcontractors(orgId: string): Promise<SubcontractorRow[]> {
  return readThroughCache<SubcontractorRow>('subcontractors', orgId, async () => {
    const { data, error } = await supabase
      .from('subcontractors')
      .select('*')
      .eq('org_id', orgId)
      .is('archived_at', null)
      .order('company_name', { ascending: true });
    if (error) throw error;
    return (data ?? []) as SubcontractorRow[];
  });
}

export async function updateSubcontractor(id: string, patch: Partial<SubcontractorRow>): Promise<void> {
  const { error } = await supabase.from('subcontractors').update(patch).eq('id', id);
  if (error) throw error;
}

export async function archiveSubcontractor(id: string): Promise<void> {
  const { error } = await supabase
    .from('subcontractors')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

// ============================================================
// Organisation helpers
// ============================================================

export async function updateOrgLogo(orgId: string, logoPath: string | null): Promise<void> {
  const { error } = await supabase
    .from('organisations')
    .update({ logo_path: logoPath })
    .eq('id', orgId);
  if (error) throw error;
}

/**
 * Rename the organisation. RLS already gates this to owners; the UI gates
 * it to owners too so the button doesn't even appear for admins/members.
 */
export async function updateOrgName(orgId: string, name: string): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('Company name cannot be empty.');
  if (trimmed.length > 120) throw new Error('Company name is too long (max 120 characters).');
  const { error } = await supabase
    .from('organisations')
    .update({ name: trimmed })
    .eq('id', orgId);
  if (error) throw error;
}

export interface OrgProfileUpdate {
  role?: 'runs_jobs' | 'works_for_others' | 'both';
  trades?: string[];
  riskyActivities?: string[];
  profileCompleted?: boolean;
  enabledFeatures?: string[];
  disabledFeatures?: string[];
}

export async function updateOrgProfile(orgId: string, p: OrgProfileUpdate): Promise<void> {
  const update: Record<string, unknown> = {};
  if (p.role !== undefined)              update.role = p.role;
  if (p.trades !== undefined)            update.trades = p.trades;
  if (p.riskyActivities !== undefined)   update.risky_activities = p.riskyActivities;
  if (p.profileCompleted !== undefined)  update.profile_completed = p.profileCompleted;
  if (p.enabledFeatures !== undefined)   update.enabled_features = p.enabledFeatures;
  if (p.disabledFeatures !== undefined)  update.disabled_features = p.disabledFeatures;
  const { error } = await supabase
    .from('organisations')
    .update(update)
    .eq('id', orgId);
  if (error) throw error;
}


// ============================================================
// RAMS library
// ============================================================

export interface RamsItem {
  id: string;
  org_id: string;
  site_id: string | null;
  title: string;
  company: string | null;
  trade: string | null;
  notes: string | null;
  file_url: string;          // storage path inside `rams-files`
  file_name: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  trade_preset: string | null;
  task_preset: string | null;
  hazard_presets: string[];
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  // Full editable form state — JSON blob populated by RamsPresetForm on
  // save so a user can come back to an issued RAMS, hit Edit, and resume
  // every tick / control / sequence-step they had typed. Stored as
  // jsonb in postgres; rendered as `unknown` here because the form
  // shape is a free-form payload owned by the form component. Null for
  // any RAMS created before this column existed.
  form_state?: unknown | null;
}

export async function listRams(siteId: string): Promise<RamsItem[]> {
  const { data, error } = await supabase
    .from('rams_library')
    .select('*')
    .eq('site_id', siteId)
    .is('archived_at', null)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as RamsItem[];
}

/**
 * Cheap "does this org have any RAMS at all" check — used by the
 * Dashboard's first-RAMS redirect to skip onboarding when a returning
 * customer (or one signing in on a new device) already has a RAMS in
 * their library. Avoids the localStorage-only check bouncing real users
 * through onboarding every time they clear their cache.
 *
 * Uses `head: true` so Supabase returns only the count, not the rows.
 */
export async function countOrgRams(orgId: string): Promise<number> {
  const { count, error } = await supabase
    .from('rams_library')
    .select('id', { count: 'exact', head: true })
    .eq('org_id', orgId)
    .is('archived_at', null);
  if (error) throw error;
  return count ?? 0;
}

export interface CreateRamsInput {
  id?: string;
  orgId: string;
  siteId: string;
  title: string;
  company: string | null;
  trade_preset: string | null;
  task_preset: string | null;
  hazard_presets: string[];
  notes: string | null;
  file_url: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  // Optional editable form snapshot — set on save so the user can hit
  // "Edit" later and resume every tick / control / sequence step.
  form_state?: unknown;
}

export async function createRams(input: CreateRamsInput): Promise<RamsItem> {
  const row: Record<string, unknown> = {
    org_id: input.orgId,
    site_id: input.siteId,
    title: input.title,
    company: input.company,
    trade: input.trade_preset,           // mirror preset into the visible trade field
    trade_preset: input.trade_preset,
    task_preset: input.task_preset,
    hazard_presets: input.hazard_presets,
    notes: input.notes,
    file_url: input.file_url,
    file_name: input.file_name,
    mime_type: input.mime_type,
    size_bytes: input.size_bytes,
  };
  if (input.id) row.id = input.id;
  if (input.form_state !== undefined) row.form_state = input.form_state;
  const { data, error } = await supabase
    .from('rams_library')
    .insert(row)
    .select()
    .single();
  if (error) throw error;
  return data as RamsItem;
}

/**
 * Fetch a single RAMS row by id. Used by the editor when re-opening an
 * existing RAMS to hydrate the form from form_state. Returns null if
 * the row doesn't exist or has been archived.
 */
export async function getRams(id: string): Promise<RamsItem | null> {
  const { data, error } = await supabase
    .from('rams_library')
    .select('*')
    .eq('id', id)
    .is('archived_at', null)
    .maybeSingle();
  if (error) throw error;
  return (data as RamsItem | null) ?? null;
}

/**
 * Update an existing RAMS — used by the in-place edit flow. The file
 * URL itself doesn't change (the PDF at the same storage path is
 * overwritten so any 30-day share links keep working), but title,
 * company, file metadata, hazard_presets and form_state may all
 * update as the user revises.
 */
export interface UpdateRamsInput {
  title?: string;
  company?: string | null;
  trade_preset?: string | null;
  hazard_presets?: string[];
  notes?: string | null;
  file_name?: string;
  size_bytes?: number;
  form_state?: unknown;
}

export async function updateRams(id: string, input: UpdateRamsInput): Promise<RamsItem> {
  const row: Record<string, unknown> = {
    updated_at: new Date().toISOString(),
  };
  if (input.title !== undefined)          row.title          = input.title;
  if (input.company !== undefined)        row.company        = input.company;
  if (input.trade_preset !== undefined) { row.trade_preset   = input.trade_preset; row.trade = input.trade_preset; }
  if (input.hazard_presets !== undefined) row.hazard_presets = input.hazard_presets;
  if (input.notes !== undefined)          row.notes          = input.notes;
  if (input.file_name !== undefined)      row.file_name      = input.file_name;
  if (input.size_bytes !== undefined)     row.size_bytes     = input.size_bytes;
  if (input.form_state !== undefined)     row.form_state     = input.form_state;
  const { data, error } = await supabase
    .from('rams_library')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as RamsItem;
}

export async function archiveRams(id: string): Promise<void> {
  const { error } = await supabase
    .from('rams_library')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

// ============================================================
// DOCUMENTS LIBRARY
// ============================================================

export type DocCategory = 'cdm' | 'policy' | 'register' | 'permit' | 'certificate' | 'custom';
export type DocScope = 'org' | 'site';
export type DocStatus = 'draft' | 'issued' | 'superseded' | 'archived' | 'expired' | 'not_applicable';

export interface DocumentRow {
  id: string;
  org_id: string;
  site_id: string | null;
  template_id: string | null;
  title: string;
  category: DocCategory;
  scope: DocScope;
  version: number;
  is_current: boolean;
  status: DocStatus;
  file_path: string | null;
  file_name: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  issued_at: string | null;
  issued_by: string | null;
  expires_at: string | null;
  notes: string | null;
  prev_version_id: string | null;
  created_at: string;
  updated_at: string;
}

/** List all current documents for an org (org-wide and site-specific). */
export async function listDocuments(orgId: string): Promise<DocumentRow[]> {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .eq('org_id', orgId)
    .eq('is_current', true)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as DocumentRow[];
}

/** List current org-wide documents only (site_id is null). */
export async function listOrgDocuments(orgId: string): Promise<DocumentRow[]> {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .eq('org_id', orgId)
    .is('site_id', null)
    .eq('is_current', true)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as DocumentRow[];
}

/** List current site-specific documents for a site. */
export async function listSiteDocuments(siteId: string): Promise<DocumentRow[]> {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .eq('site_id', siteId)
    .eq('is_current', true)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as DocumentRow[];
}

/** Get version history for a template within an org/site (current first). */
export async function listDocumentVersions(
  orgId: string,
  templateId: string,
  siteId: string | null,
): Promise<DocumentRow[]> {
  let q = supabase
    .from('documents')
    .select('*')
    .eq('org_id', orgId)
    .eq('template_id', templateId)
    .order('version', { ascending: false });
  q = siteId === null ? q.is('site_id', null) : q.eq('site_id', siteId);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as DocumentRow[];
}

/** Get a single document. */
export async function getDocument(id: string): Promise<DocumentRow | null> {
  const { data, error } = await supabase.from('documents').select('*').eq('id', id).single();
  if (error) return null;
  return data as DocumentRow;
}

/**
 * Create a new document.
 * If supersedeId is provided, the previous row is marked superseded and the
 * new row inherits the next version number.
 */
export async function createDocument(input: {
  id?: string;
  orgId: string;
  siteId: string | null;
  templateId: string | null;
  title: string;
  category: DocCategory;
  scope: DocScope;
  status?: DocStatus;
  filePath: string | null;
  fileName: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  expiresAt?: string | null;
  notes?: string | null;
  supersedeId?: string | null;
}): Promise<DocumentRow> {
  let version = 1;
  let prevId: string | null = null;
  if (input.supersedeId) {
    const prev = await getDocument(input.supersedeId);
    if (prev) {
      version = prev.version + 1;
      prevId = prev.id;
      // mark prev superseded
      const { error: e1 } = await supabase
        .from('documents')
        .update({ is_current: false, status: 'superseded' })
        .eq('id', prev.id);
      if (e1) throw e1;
    }
  }
  const row: Record<string, unknown> = {
    org_id: input.orgId,
    site_id: input.siteId,
    template_id: input.templateId,
    title: input.title,
    category: input.category,
    scope: input.scope,
    version,
    is_current: true,
    status: input.status ?? 'issued',
    file_path: input.filePath,
    file_name: input.fileName,
    mime_type: input.mimeType,
    size_bytes: input.sizeBytes,
    expires_at: input.expiresAt ?? null,
    notes: input.notes ?? null,
    prev_version_id: prevId,
    issued_at: new Date().toISOString(),
  };
  if (input.id) row.id = input.id;
  const { data, error } = await supabase.from('documents').insert(row).select().single();
  if (error) throw error;
  return data as DocumentRow;
}

/** Soft-archive a document (sets status='archived', is_current=false). */
export async function archiveDocument(id: string): Promise<void> {
  const { error } = await supabase
    .from('documents')
    .update({ is_current: false, status: 'archived' })
    .eq('id', id);
  if (error) throw error;
}

/**
 * Mark a template as "not required" for an org or site.
 * Creates (or updates) a documents row with status='not_applicable', is_current=true,
 * and writes a note containing the reason and who marked it.
 */
export async function markDocumentNotApplicable(input: {
  orgId: string;
  siteId: string | null;
  templateId: string;
  title: string;
  category: DocCategory;
  scope: DocScope;
  reason: string;
}): Promise<DocumentRow> {
  // First, mark any existing current row for this template superseded
  let q = supabase
    .from('documents')
    .update({ is_current: false, status: 'superseded' })
    .eq('org_id', input.orgId)
    .eq('template_id', input.templateId)
    .eq('is_current', true);
  if (input.siteId === null) q = q.is('site_id', null);
  else q = q.eq('site_id', input.siteId);
  await q;

  const noteBody = input.reason.trim()
    ? `Marked NOT REQUIRED: ${input.reason.trim()}`
    : 'Marked NOT REQUIRED for this site / org.';

  const { data, error } = await supabase
    .from('documents')
    .insert({
      org_id: input.orgId,
      site_id: input.siteId,
      template_id: input.templateId,
      title: input.title,
      category: input.category,
      scope: input.scope,
      version: 1,
      is_current: true,
      status: 'not_applicable',
      notes: noteBody,
    })
    .select()
    .single();
  if (error) throw error;
  return data as DocumentRow;
}

/**
 * Reverse the "not required" decision — set the row to archived so the
 * template card flips back to "Not started".
 */
export async function unmarkDocumentNotApplicable(documentId: string): Promise<void> {
  const { error } = await supabase
    .from('documents')
    .update({ is_current: false, status: 'archived' })
    .eq('id', documentId);
  if (error) throw error;
}

// ============================================================
// PERMITS
// ============================================================

export type PermitType = 'hot_works' | 'dig' | 'confined_space' | 'height' | 'lock_out_tag_out' | 'custom';
export type PermitStatus = 'open' | 'active' | 'closed' | 'expired' | 'cancelled';

export interface PermitRow {
  id: string;
  org_id: string;
  site_id: string;
  template_id: string;
  title: string;
  permit_type: PermitType;
  issued_to_name: string;
  issued_to_company: string | null;
  issued_by: string | null;
  issued_at: string;
  expires_at: string;
  status: PermitStatus;
  closed_at: string | null;
  closed_by: string | null;
  fire_watch_minutes: number | null;
  notes: string | null;
  file_path: string | null;
  file_name: string | null;
  details: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/** List active / open permits for a site. */
export async function listActivePermits(siteId: string): Promise<PermitRow[]> {
  const { data, error } = await supabase
    .from('permits')
    .select('*')
    .eq('site_id', siteId)
    .in('status', ['open', 'active'])
    .order('issued_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as PermitRow[];
}

/** List permit history for a site. */
export async function listAllPermits(siteId: string, limit = 200): Promise<PermitRow[]> {
  const { data, error } = await supabase
    .from('permits')
    .select('*')
    .eq('site_id', siteId)
    .order('issued_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as PermitRow[];
}

/** Get a permit. */
export async function getPermit(id: string): Promise<PermitRow | null> {
  const { data, error } = await supabase.from('permits').select('*').eq('id', id).single();
  if (error) return null;
  return data as PermitRow;
}

/** Create a permit (status defaults to 'open'). */
export async function createPermit(input: {
  id?: string;
  orgId: string;
  siteId: string;
  templateId: string;
  title: string;
  permitType: PermitType;
  issuedToName: string;
  issuedToCompany?: string | null;
  expiresAt: string;
  fireWatchMinutes?: number | null;
  notes?: string | null;
  filePath?: string | null;
  fileName?: string | null;
  details?: Record<string, unknown>;
}): Promise<PermitRow> {
  const row: Record<string, unknown> = {
    org_id: input.orgId,
    site_id: input.siteId,
    template_id: input.templateId,
    title: input.title,
    permit_type: input.permitType,
    issued_to_name: input.issuedToName,
    issued_to_company: input.issuedToCompany ?? null,
    expires_at: input.expiresAt,
    status: 'open',
    fire_watch_minutes: input.fireWatchMinutes ?? null,
    notes: input.notes ?? null,
    file_path: input.filePath ?? null,
    file_name: input.fileName ?? null,
    details: input.details ?? {},
  };
  if (input.id) row.id = input.id;
  const { data, error } = await supabase.from('permits').insert(row).select().single();
  if (error) throw error;
  return data as PermitRow;
}

/** Mark a permit closed. */
export async function closePermit(id: string, opts?: { notes?: string }): Promise<void> {
  const patch: Record<string, unknown> = {
    status: 'closed',
    closed_at: new Date().toISOString(),
  };
  if (opts?.notes !== undefined) patch.notes = opts.notes;
  const { error } = await supabase.from('permits').update(patch).eq('id', id);
  if (error) throw error;
}

/** Mark a permit cancelled (e.g. wrong issue, work didn't go ahead). */
export async function cancelPermit(id: string): Promise<void> {
  const { error } = await supabase
    .from('permits')
    .update({ status: 'cancelled', closed_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}
